import { Injectable, inject } from '@angular/core';
import { ContractListItem } from '@app/features/contracts/contract.model';
import {
  SignatureSendFailure,
  YousignEvent,
} from '@app/features/contracts/signature/signature-request.model';
import { SignatureService } from '@app/features/contracts/signature/signature-service';
import { Observable, combineLatest, map } from 'rxjs';
import { ContractAmendmentsHistory } from './contract-amendments-history';
import {
  ContractHistorySource,
  HistoryEntry,
  HistoryOrigin,
  HistorySubject,
} from './history-entry.model';

export const SIGNATURE_ORIGIN: HistoryOrigin = { key: 'signature', label: 'Signature' };

/** Les événements que Yousign envoie, dans le vocabulaire de l'utilisateur. */
const eventLabels: Record<string, string> = {
  'signature_request.activated': 'Demande de signature envoyée',
  'signature_request.reactivated': 'Demande de signature rouverte',
  'signature_request.done': 'Signé par toutes les parties',
  'signature_request.approved': 'Approuvé par le signataire',
  'signature_request.declined': 'Refusé par un signataire',
  'signature_request.rejected': 'Demande de signature rejetée par Yousign',
  'signature_request.expired': 'Demande de signature expirée',
  'signature_request.canceled': 'Demande de signature annulée',
  'signature_request.deleted': 'Demande de signature supprimée',
  'signature_request.permanently_deleted': 'Demande de signature supprimée définitivement',
  'signature_request.reminder_executed': 'Relance envoyée',
  'signature_request.automatic_reminder_executed': 'Relance automatique envoyée',
};

const title = (event: YousignEvent) =>
  eventLabels[event.event] ?? `Événement Yousign « ${event.event} »`;

/**
 * Les événements Yousign des demandes de signature du contrat, et les envois qui ont échoué avant
 * d'en créer une : ceux de son embauche et ceux de ses avenants, marqués de leur avenant.
 */
@Injectable()
export class SignatureHistorySource implements ContractHistorySource {
  readonly origin = SIGNATURE_ORIGIN;

  private readonly service = inject(SignatureService);
  private readonly amendments = inject(ContractAmendmentsHistory);

  entries(contract: ContractListItem): Observable<HistoryEntry[]> {
    return combineLatest([
      this.service.yousignEvents(contract.id),
      this.service.sendFailures(contract.id),
      this.amendments.of(contract),
    ]).pipe(
      map(([events, failures, { subjectOf }]) => [
        ...events.map((event, index) => this.event(event, index, subjectOf(event.subjectId))),
        ...failures.map((failure, index) =>
          this.failure(failure, index, subjectOf(failure.subjectId)),
        ),
      ]),
    );
  }

  private event(event: YousignEvent, index: number, subject?: HistorySubject): HistoryEntry {
    return {
      id: `signature-${index}-${event.subjectId}-${event.event}-${event.at}`,
      origin: SIGNATURE_ORIGIN,
      ...(subject ? { subject } : {}),
      at: event.at,
      title: [title(event)],
    };
  }

  /** RG-signature-echec-visible : quand, par qui, et pourquoi, en mots. */
  private failure(
    failure: SignatureSendFailure,
    index: number,
    subject?: HistorySubject,
  ): HistoryEntry {
    return {
      id: `signature-echec-${index}-${failure.subjectId}-${failure.failedAt}`,
      origin: SIGNATURE_ORIGIN,
      ...(subject ? { subject } : {}),
      at: failure.failedAt,
      title: ['Envoi en signature échoué'],
      lines: [
        { text: [`Lancé par ${failure.failedBy}`] },
        { text: [failure.reason], tone: 'danger' },
      ],
    };
  }
}
